<script setup>
import { useHelpersStore } from "@/aygulyazilim/store/useHelpers"
import misc404 from '@images/pages/404_gis.png'

const storeHelpers = useHelpersStore()
const appVersion = import.meta.env.VITE_APP_VERSION

definePage({
  alias: '/pages/misc/not-found/:error(.*)',
  meta: {
    layout: 'blank',
    public: true,
  },
})
</script>

<template>
  <div class="login-page-container">
    <div class="login-card shadow-2xl">
      <div class="accent-bar" />

      <div class="login-card-content pa-8 pa-md-12">
        <div class="login-logo-section mb-6 text-center">
          <RouterLink to="/" class="d-inline-flex flex-column align-center justify-center gap-3 no-underline">
            <div class="logo-wrapper">
              <img :src="`/logo.svg?v=${appVersion}`" class="app-logo-img">
            </div>
            <div class="d-flex flex-column align-center">
              <span class="brand-name mb-1">{{ storeHelpers.config.firma_kisa??storeHelpers.config.firma_adi }}</span>
              <h1 class="application-title">
                {{ $t("auth.Application Title") }}
              </h1>
            </div>
          </RouterLink>
        </div>

        <div class="text-center mb-10">
          <div class="error-code-badge mb-4">
            404
          </div>
          <h2 class="welcome-title mb-2">
            Sayfa Bulunamadı ⚠️
          </h2>
          <p class="welcome-subtitle">
            Aradığınız sayfa mevcut değil veya taşınmış olabilir.
          </p>
        </div>

        <div class="misc-avatar w-100 text-center mb-8">
          <VImg
            :src="misc404"
            alt="error 404"
            max-height="250"
            class="mx-auto drop-shadow"
          />
        </div>

        <VBtn
          to="/"
          block
          size="x-large"
          class="premium-btn mb-6"
        >
          Ana Sayfaya Dön
        </VBtn>
      </div>

      <div class="login-footer py-6 text-center">
        <p class="copyright-text">
          🌹 <VIcon icon="tabler-copyright" color="error" size="1.25rem" class="mx-1" /> 2024 &nbsp;
          <a href="https://aygulyazilim.com" target="_blank" rel="noopener noreferrer">Aygül Yazılım</a> 
          <VIcon icon="tabler-heart-filled" color="error" size="1.25rem" class="mx-1" /> 
          &nbsp;{{ $t("Her hakkı saklıdır") }}
        </p>
      </div>
    </div>
  </div>
</template>

<style lang="scss">
@use "@core/scss/template/pages/page-auth";

.login-page-container {
  position: relative;
  display: flex;
  overflow: hidden;
  align-items: center;
  justify-content: center;
  padding: 1.5rem;
  background: #0f172a url("/images/background/gis_tracking_bg.png") center/cover no-repeat fixed !important;
  font-family: "Public Sans", sans-serif;
  min-block-size: 100vh;

  &::before {
    position: absolute;
    z-index: 1;
    background: radial-gradient(circle at center, rgba(15, 23, 42, 10%), rgba(15, 23, 42, 80%));
    content: "";
    inset: 0;
  }
}

.login-card {
  position: relative;
  z-index: 10;
  display: flex;
  overflow: hidden;
  flex-direction: column;
  border: 1px solid rgba(255, 255, 255, 20%);
  border-radius: 32px;
  animation: cardEntrance 0.8s cubic-bezier(0.16, 1, 0.3, 1), cardFloat 6s ease-in-out infinite 0.8s;
  backdrop-filter: blur(40px) saturate(180%);
  background: rgba(255, 255, 255, 90%) !important;
  box-shadow: 0 50px 100px -20px rgba(0, 0, 0, 40%);
  color: #010413 !important;
  inline-size: 100%;
  max-inline-size: 540px;
}

@keyframes cardEntrance {
  from {
    opacity: 0;
    transform: translateY(30px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

@keyframes cardFloat {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-10px); }
}

.accent-bar {
  background: linear-gradient(90deg, #dc2626, #991b1b);
  block-size: 6px;
  inline-size: 100%;
}

.brand-name {
  background: linear-gradient(135deg, #0f172a, #334155);
  background-clip: text;
  font-size: 1.8rem;
  font-weight: 800;
  letter-spacing: -1px;
  line-height: 1;
  -webkit-text-fill-color: transparent;
}

.application-title {
  color: #1e293b;
  font-size: 1.3rem;
  font-weight: 700;
  letter-spacing: -0.5px;
  line-height: 1.2;
}

.logo-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px;
  border-radius: 14px;
  background: white;
  box-shadow: 0 10px 20px rgba(0, 0, 0, 8%);
  img {
    block-size: 44px;
    inline-size: auto;
  }
}

.welcome-title {
  color: #0f172a;
  font-size: 2.2rem;
  font-weight: 800;
  letter-spacing: -0.5px;
}

.welcome-subtitle {
  color: #64748b;
  font-size: 1.1rem;
  font-weight: 500;
}

.error-code-badge {
  display: inline-block;
  padding: 0.5rem 1.5rem;
  border-radius: 12px;
  background: rgba(220, 38, 38, 10%);
  color: #dc2626;
  font-size: 1.25rem;
  font-weight: 800;
  letter-spacing: 2px;
}

.premium-btn {
  border-radius: 16px !important;
  background: linear-gradient(135deg, #ef4444, #dc2626) !important;
  block-size: 64px !important;
  box-shadow: 0 10px 30px -5px rgba(220, 38, 38, 40%) !important;
  color: #fff !important;
  font-size: 1.15rem !important;
  font-weight: 800 !important;
  text-transform: none !important;

  &:hover {
    box-shadow: 0 15px 40px -5px rgba(220, 38, 38, 50%) !important;
    transform: translateY(-2px);
  }
}

.drop-shadow {
  filter: drop-shadow(0 20px 30px rgba(0, 0, 0, 0.15));
}

.login-footer {
  background: rgba(248, 250, 252, 50%);
  border-block-start: 1px solid rgba(0, 0, 0, 5%);
}

.copyright-text {
  margin: 0;
  color: #334155;
  font-size: 0.85rem;
  font-weight: 600;
}

@media (max-width: 600px) {
  .login-card {
    border-radius: 20px;
  }
  .welcome-title {
    font-size: 1.8rem;
  }
}
</style>
